import { useSyncExternalStore } from "react";
import type { AgenturGuide, CreatorGuide, StationId } from "./coco-rundgang-texte";
import type { ModulKey } from "../../model/tarif";

/**
 * Rundgang mit Coco (Inhaber 20.10.2026: „die Leute müssen besser abgeholt werden“). Rein und ohne Datenbank, damit Tests die
 * Regeln prüfen: welche Stationen eine Rolle sieht, wohin sie führen und welchen Knopf sie hervorheben (data-coco an der Seite),
 * wann der Rundgang von selbst startet, was gespeichert wird (user_preferences.onboarding.rundgang, wie die Karten „Coco erklärt“)
 * und welche Seiten danach als erklärt gelten. Die Oberfläche steht in components/CocoRundgang.tsx, die Texte in
 * coco-rundgang-texte.ts. Fehlt ein Ziel auf der Seite (Modul oder Recht fehlt), zeigt die Oberfläche die Station ohne
 * Hervorhebung; fehlt das Recht für die ganze Seite, fällt die Station hier schon weg.
 */

/** studio: eigenes Creator-Konto (organizations.kind = creator, Tarife Creator und Creator Pro; K-557) */
export type RundgangRolle = "admin" | "team" | "creator" | "studio";

export interface Station {
  id: StationId;
  /** Seite der Station; null: auf der aktuellen Seite bleiben (Abschluss) */
  pfad: string | null;
  /** Wert von data-coco am hervorgehobenen Element; null: ohne Hervorhebung */
  ziel: string | null;
  /** Schlüssel der Seitenhilfe, die nach dieser Station als erklärt gilt (agentur_… bzw. Creator-Schlüssel) */
  seite: string | null;
}

/** Rechte der Person, soweit sie Stationen betreffen (Agentur); Creator haben keine */
export interface RundgangKontext { betraege: boolean; auszahlungen: boolean }

type Def = Station & { braucht?: (k: RundgangKontext) => boolean };
const g = (key: AgenturGuide) => `agentur_${key}`;
const c = (key: CreatorGuide) => key;

const STATIONEN: Record<RundgangRolle, Def[]> = {
  // K-557 (Inhaber 20.10.2026): Skepsis nehmen, dann in der Reihenfolge der Startliste: zuerst Creator, dann die eigenen Daten,
  // Marke, Kooperation (mit Content-Freigabe als Satz), Vertrag, Rechnung, Auszahlung; höchstens 9 Schritte
  admin: [
    { id: "willkommen", pfad: "/agentur", ziel: "start-liste", seite: g("overview") },
    { id: "creator", pfad: "/agentur/creator", ziel: "creator-anlegen", seite: g("creator") },
    { id: "daten", pfad: "/agentur/einstellungen/firma", ziel: "firmendaten", seite: g("einstellungen") },
    { id: "marke", pfad: "/agentur/marken", ziel: "marke-anlegen", seite: g("marken") },
    { id: "kooperation", pfad: "/agentur/kooperationen", ziel: "kooperation-anlegen", seite: g("kooperationen") },
    { id: "vertrag", pfad: "/agentur/vertraege", ziel: "vertrag-erstellen", seite: g("vertraege") },
    { id: "rechnung", pfad: "/agentur/rechnungen", ziel: "rechnung-erstellen", seite: g("rechnungen"), braucht: (k) => k.betraege },
    { id: "auszahlung", pfad: "/agentur/auszahlungen", ziel: "auszahlung-lauf", seite: g("auszahlungen"), braucht: (k) => k.betraege && k.auszahlungen },
    // K-573: Abschluss auf der Übersicht mit sichtbarer Liste „So startest du“ (vorher auf der Seite der letzten Station)
    { id: "abschluss", pfad: "/agentur", ziel: "start-liste", seite: null },
  ],
  // K-557: eigenes Creator-Konto (bisher ohne Rundgang): eigene Angaben, Marke, Deal, Vertrag, Rechnung
  studio: [
    { id: "s-willkommen", pfad: "/agentur", ziel: "start-liste", seite: g("overview") },
    { id: "s-daten", pfad: "/agentur/einstellungen/firma", ziel: "firmendaten", seite: g("einstellungen") },
    { id: "s-marke", pfad: "/agentur/marken", ziel: "marke-anlegen", seite: g("marken") },
    { id: "s-deal", pfad: "/agentur/kooperationen", ziel: "kooperation-anlegen", seite: g("kooperationen") },
    { id: "s-vertrag", pfad: "/agentur/vertraege", ziel: "vertrag-erstellen", seite: g("vertraege") },
    { id: "s-rechnung", pfad: "/agentur/rechnungen", ziel: "rechnung-erstellen", seite: g("rechnungen") },
    { id: "s-abschluss", pfad: "/agentur", ziel: "start-liste", seite: null },
  ],
  team: [
    { id: "team-willkommen", pfad: "/agentur", ziel: null, seite: g("overview") },
    { id: "team-kooperationen", pfad: "/agentur/kooperationen", ziel: "kooperation-anlegen", seite: g("kooperationen") },
    { id: "team-vertraege", pfad: "/agentur/vertraege", ziel: "vertrag-erstellen", seite: g("vertraege") },
    { id: "team-chat", pfad: "/agentur/nachrichten", ziel: "chat-liste", seite: g("nachrichten") },
  ],
  creator: [
    { id: "c-willkommen", pfad: "/creator", ziel: null, seite: c("overview") },
    { id: "c-deals", pfad: "/creator/deals", ziel: "deals-liste", seite: c("deals") },
    { id: "c-rechnung", pfad: "/creator/rechnungen", ziel: "rechnung-einreichen", seite: c("invoices") },
    { id: "c-vertraege", pfad: "/creator/vertraege", ziel: "vertraege-liste", seite: c("contracts") },
    { id: "c-chat", pfad: "/creator/nachrichten", ziel: "chat-liste", seite: c("chat") },
    { id: "c-profil", pfad: "/creator/profil", ziel: "profil-auszahlung", seite: c("profile") },
  ],
};

/**
 * K-551: Stationen, deren Funktion erst ein größerer Tarif enthält. Im Rundgang bleibt die Station (sie zeigt, was geht),
 * der Titel bekommt aber „(ab Team)“, wenn der Tarif der Agentur das Modul nicht hat.
 */
export const STATION_MODUL: Partial<Record<StationId, ModulKey>> = { auszahlung: "module_payouts" };
/** K-557: Content-Freigabe ist ein Satz in der Station Kooperation bzw. Deal; „(ab Team)“ dort am Satz, wenn das Modul fehlt */
export const FREIGABE_MODUL: ModulKey = "module_brand_review";

const ALLE_RECHTE: RundgangKontext = { betraege: true, auszahlungen: true };

/** Stationen einer Rolle; Seiten ohne Recht fallen weg (nie eine Station, die auf „kein Zugriff“ führt) */
export function stationenFuer(rolle: RundgangRolle, k: RundgangKontext = ALLE_RECHTE): Station[] {
  return STATIONEN[rolle].filter((s) => !s.braucht || s.braucht(k)).map(({ braucht: _b, ...s }) => s);
}

/** Wer welchen Rundgang bekommt: Admin der Agentur, Team ohne Admin, das eigene Creator-Konto (0178) den kurzen Studio-Rundgang (K-557) */
export function rolleFuer(ws: { isChef: boolean; isCreator?: boolean }): RundgangRolle | null {
  if (ws.isCreator) return "studio";
  return ws.isChef ? "admin" : "team";
}

/** Selektor des hervorgehobenen Elements */
export const zielSelektor = (ziel: string) => `[data-coco="${ziel.replace(/"/g, "")}"]`;
/**
 * K-573: Leerer Zustand zum Ziel (data-coco-leer mit demselben Wert, nur gesetzt, solange die Liste leer ist bzw. der Knopf noch
 * nichts tun kann). Steht er auf der Seite, hebt die Station ihn statt des Knopfs hervor und erklärt, was dort später erscheint.
 */
export const leerSelektor = (ziel: string) => `[data-coco-leer="${ziel.replace(/"/g, "")}"]`;

/** Pose von Coco je Station (K-573): zur Begrüßung winkend und groß, zum Abschluss freudig, vor leerem Bereich mit Hinweis */
export function stationPose(index: number, von: number, leer: boolean): "winkt" | "freut-sich" | "hinweis" | "erklaert" {
  if (index === 0) return "winkt";
  if (index === von - 1) return "freut-sich";
  return leer ? "hinweis" : "erklaert";
}

// ── Fortschritt (user_preferences.onboarding.rundgang) ───────────────────────

export type RundgangStatus = "laufend" | "beendet" | "uebersprungen";
export interface RundgangStand {
  status: RundgangStatus;
  /** zuletzt gezeigte Station (1 = erste) und Zahl der Stationen */
  schritt: number;
  von: number;
  /** gezeigte Stationen */
  erledigt: StationId[];
  am: string;
  /** K-591: in Schritt 1 „Mit Beispieldaten zeigen“ gewählt (nach dem Neuladen fragt der Abschluss weiter „behalten oder entfernen?“) */
  beispiel?: boolean;
}
interface MitRundgang { intro_off?: boolean; guides?: Partial<Record<string, boolean>>; rundgang?: Partial<Record<RundgangRolle, RundgangStand>>; [key: string]: unknown }

export function rundgangStand(o: MitRundgang | null | undefined, rolle: RundgangRolle): RundgangStand | null {
  const s = o?.rundgang?.[rolle];
  return s && typeof s === "object" && typeof s.status === "string" ? s : null;
}

/** Hat die Person auf diesem Portal schon eine Seitenhilfe bestätigt? (dann ist sie nicht neu) */
function kenntSeiten(o: MitRundgang, rolle: RundgangRolle): boolean {
  return Object.entries(o.guides ?? {}).some(([k, v]) => v && (rolle === "creator" ? !k.startsWith("agentur_") : k.startsWith("agentur_")));
}

const STARTSEITEN: Record<RundgangRolle, readonly string[]> = {
  admin: ["/agentur", "/agentur/schnellstart"],
  team: ["/agentur"],
  creator: ["/creator"],
  studio: ["/agentur"],
};

/**
 * Startet der Rundgang von selbst? Nur beim ersten Login: noch kein Rundgang (auch kein übersprungener), Einführung nicht
 * ausgeschaltet, noch keine Seitenhilfe bestätigt, auf der Startseite (nie mitten in einem Link aus einer Mail), nicht in der
 * Nur-Lesen-Phase und nicht eingebettet. Beim Admin zusätzlich: noch keine eigene Kooperation (sonst kennt er das Portal schon);
 * solange das unbekannt ist, wartet er.
 */
export function sollAutoStarten(input: {
  rolle: RundgangRolle | null; onboarding: MitRundgang | null | undefined; pfad: string; nurLesen?: boolean; eingebettet?: boolean;
  ohneKooperation?: boolean | null;
}): boolean {
  const { rolle, onboarding: o } = input;
  if (!rolle || !o || input.nurLesen || input.eingebettet || o.intro_off) return false;
  if (rundgangStand(o, rolle) || kenntSeiten(o, rolle)) return false;
  if (!STARTSEITEN[rolle].includes(input.pfad.replace(/\/+$/, "") || "/")) return false;
  if ((rolle === "admin" || rolle === "studio") && input.ohneKooperation !== true) return false;
  return true;
}

/**
 * Was gespeichert wird: Stand des Rundgangs dieser Rolle und die Seiten der gezeigten Stationen als erklärt, damit „Coco erklärt“
 * dort nicht gleich noch einmal aufgeht (das Coco-Symbol holt die Erklärung jederzeit zurück). index: zuletzt gezeigte Station (0 = erste).
 */
export function rundgangPatch(o: MitRundgang | null | undefined, rolle: RundgangRolle, stationen: readonly Station[], status: RundgangStatus, index: number, jetzt: string,
  extra: { beispiel?: boolean } = {}): {
  rundgang: Partial<Record<RundgangRolle, RundgangStand>>; guides: Record<string, boolean>;
} {
  const bis = status === "beendet" ? stationen.length - 1 : Math.max(0, Math.min(index, stationen.length - 1));
  const gezeigt = stationen.slice(0, bis + 1);
  const guides: Record<string, boolean> = {};
  if (status !== "laufend") for (const s of gezeigt) if (s.seite) guides[s.seite] = true;
  const stand: RundgangStand = { status, schritt: bis + 1, von: stationen.length, erledigt: gezeigt.map((s) => s.id), am: jetzt };
  if (status === "laufend" && extra.beispiel) stand.beispiel = true;
  return { rundgang: { ...(o?.rundgang ?? {}), [rolle]: stand }, guides };
}

/** K-591: So lange nach dem letzten gespeicherten Schritt gilt ein laufender Rundgang auf jeder Seite als „mitten drin“ (Neuladen) */
export const FORTSETZEN_MS = 12 * 60 * 60 * 1000;

/**
 * K-591: Neuladen mitten im Rundgang beendete ihn stumm (Stand blieb „laufend“, kein Dialog mehr). Steht der Rundgang dieser
 * Rolle auf „laufend“, geht es beim gespeicherten Schritt weiter (Rückgabe: Index, 0 = erste Station), sonst null. Liegt der letzte
 * Schritt länger zurück (Fenster geschlossen und Tage später über einen Link aus einer Mail zurück), nur auf der Startseite.
 * Nie in der Nur-Lesen-Phase, eingebettet oder mit ausgeschalteter Einführung.
 */
export function fortsetzSchritt(input: {
  rolle: RundgangRolle | null; onboarding: MitRundgang | null | undefined; anzahl: number; pfad: string; jetzt: number;
  nurLesen?: boolean; eingebettet?: boolean;
  /** Seiten der Stationen: frisch fortgesetzt wird nur auf der Startseite oder auf der Seite der gespeicherten Station */
  stationPfade?: readonly (string | null | undefined)[];
}): number | null {
  const { rolle, onboarding: o } = input;
  if (!rolle || !o || input.nurLesen || input.eingebettet || o.intro_off || input.anzahl < 1) return null;
  const s = rundgangStand(o, rolle);
  if (!s || s.status !== "laufend") return null;
  const am = Date.parse(s.am ?? "");
  const frisch = Number.isFinite(am) && input.jetzt - am <= FORTSETZEN_MS;
  const pfad = input.pfad.replace(/\/+$/, "") || "/";
  const schritt = Math.floor(Number(s.schritt));
  const index = Number.isFinite(schritt) && schritt >= 1 ? Math.min(schritt, input.anzahl) - 1 : 0;
  const aufStart = STARTSEITEN[rolle].includes(pfad);
  if (!frisch && !aufStart) return null;
  // Prüf-Chat 30.10.2026: ein Link aus einer Mail (andere Seite, voll geladen) wurde sonst auf die Station umgeleitet
  if (frisch && !aufStart && input.stationPfade) {
    const ziel = input.stationPfade[index];
    if (ziel && ziel !== pfad) return null;
  }
  return index;
}

/** Ereignis für die Messung im Betrieb (0380): nur Zählwerte */
export type RundgangEreignis = "started" | "finished" | "skipped";
export function ereignisArgs(orgId: string, rolle: RundgangRolle, ereignis: RundgangEreignis, schritt: number | null, von: number) {
  return { p_org: orgId, p_role: rolle, p_event: ereignis, p_step: schritt, p_steps: von };
}

// ── Seitenhilfe: Weg zum ersten Anlegen und Beispieldaten ────────────────────

export interface SeitenKontext { chef: boolean; studio: boolean; betraege: boolean; pfad?: string }

/** Ziel des Knopfs „was legst du als Erstes an“; null, wenn es keinen Weg gibt oder das Recht fehlt */
export function seitenZiel(key: AgenturGuide, k: SeitenKontext): string | null {
  const ziel = ((): string | null => {
    switch (key) {
      case "overview": return k.chef && !k.studio ? "/agentur/schnellstart" : null;
      case "creator": return k.studio ? null : "/agentur/creator/einladen";
      case "kooperationen": return "/agentur/kooperationen/neu";
      case "anfragen": return "/agentur/anfragen/neu";
      case "vertraege": return k.betraege ? "/agentur/vertraege/neu" : null;
      case "rechnungen": return k.betraege ? "/agentur/rechnungen/neu" : null;
      case "einstellungen": return k.chef ? "/agentur/einstellungen/firma" : null;
      default: return null;
    }
  })();
  return ziel && ziel !== k.pfad?.replace(/\/+$/, "") ? ziel : null;
}

/** Stand der Beispieldaten (sample_data_status, 0325) */
export interface BeispielStand { has_sample: boolean; creators: number; deals: number }

/**
 * Beispieldaten anbieten? Nur der Admin einer Agentur (nicht im Creator-Konto, nicht in der Nur-Lesen-Phase), nur solange es noch
 * keine gibt; auf der Seite nur bei leerer Liste (ohne eigene Creator bzw. ohne eigene Kooperation, aus der Verträge und Rechnungen
 * entstehen) und nur dort, wo der Vertrieb einen Satz für die leere Liste hat. key null: am Ende des Rundgangs.
 */
export function beispielAnbieten(key: AgenturGuide | null, k: { chef: boolean; studio: boolean; nurLesen?: boolean }, stand: BeispielStand | null | undefined): boolean {
  if (!k.chef || k.studio || k.nurLesen || !stand || stand.has_sample) return false;
  switch (key) {
    case null: return true;
    case "creator": return stand.creators === 0;
    case "kooperationen": case "vertraege": case "rechnungen": return stand.deals === 0;
    default: return false;
  }
}

/** Wo das Coco-Symbol am Handy stört (Eingabeleiste unten): im Chat */
export function lugtStoert(pfad: string): boolean {
  return /^\/(agentur|creator)\/nachrichten(\/|$)/.test(pfad);
}

// ── Laufender Rundgang (für alle Bausteine des Rahmens) ──────────────────────

/** beispiel (K-573): in Schritt 1 „Mit Beispieldaten zeigen“ gewählt; am Ende fragt der Abschluss „behalten oder entfernen?“ */
export interface Lauf { rolle: RundgangRolle; schritt: number; quelle: "auto" | "hand" | "resume"; nr: number; beispiel?: boolean }
let lauf: Lauf | null = null;
let zaehler = 0;
const hoerer = new Set<() => void>();
const melden = () => { for (const h of hoerer) h(); };

/** quelle „resume“ (K-591): nach dem Neuladen beim gespeicherten Schritt fortsetzen, ohne neuen Start zu messen */
export function rundgangStarten(rolle: RundgangRolle, quelle: Lauf["quelle"] = "hand", ab: { schritt?: number; beispiel?: boolean } = {}): void {
  lauf = { rolle, schritt: Math.max(0, ab.schritt ?? 0), quelle, nr: ++zaehler, ...(ab.beispiel ? { beispiel: true } : {}) };
  melden();
}
export function rundgangSchritt(schritt: number): void {
  if (!lauf) return;
  lauf = { ...lauf, schritt };
  melden();
}
export function rundgangMitBeispiel(): void {
  if (!lauf) return;
  lauf = { ...lauf, beispiel: true };
  melden();
}
export function rundgangEnde(): void {
  lauf = null;
  melden();
}
export function rundgangJetzt(): Lauf | null {
  return lauf;
}
export function useRundgang(): Lauf | null {
  return useSyncExternalStore((cb) => { hoerer.add(cb); return () => { hoerer.delete(cb); }; }, () => lauf, () => null);
}
